import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/admin/AdminHeader';
import { getApiErrorMessage, logError } from '../../utils/apiError';
import authService from '../../services/authService';
import api from '../../services/api';

function PluginPage() {
    const [adminUser, setAdminUser] = useState(null);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [partners, setPartners] = useState([]);
    const [selectedPartnerId, setSelectedPartnerId] = useState('');
    const [pluginSettings, setPluginSettings] = useState({
        colorScheme: 'blue-purple',
        flowAlias: 'Guides',
        position: {
            right: '20px',
            bottom: '20px'
        },
        enableFlows: true,
        enableReferrals: true,
        enableRewards: true,
        enableSupport: true,
        overlay: {
            enabled: false,
            requirePassword: false,
            password: '',
            showTeaser: true,
            teaserMessage: 'Welcome! Enter your access code to unlock exclusive features.'
        }
    });
    const [flowConfigs, setFlowConfigs] = useState([]);
    const [dashboardData, setDashboardData] = useState(null);
    const [activity, setActivity] = useState([]);
    const [flowStats, setFlowStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    
    const navigate = useNavigate();

    useEffect(() => {
        // Check if user is logged in as super admin
        if (!authService.isAdminLoggedIn()) {
            navigate('/admin/login');
            return;
        }
        
        const userData = authService.getAdminUser();
        if (!userData || userData.role !== 'super') {
            navigate('/admin');
            return;
        }
        
        setAdminUser(userData);
        loadInitialData();
    }, [navigate]);

    const loadInitialData = async () => {
        await Promise.all([
            loadDashboard(),
            loadPartners(),
            loadFlowConfigs(),
            loadActivity()
        ]);
    };

    const loadDashboard = async () => {
        try {
            const response = await api.getPluginDashboard();
            setDashboardData(response.data.data || response.data);
        } catch (error) {
            logError('PluginPage - Failed to load dashboard', error);
            setError('Failed to load dashboard data: ' + getApiErrorMessage(error));
        }
    };

    const loadPartners = async () => {
        try {
            setLoading(true);
            const response = await api.getPluginPartners();
            setPartners(response.data.data?.partners || []);
        } catch (error) {
            console.error('Partners API error:', error);
            logError('PluginPage - Failed to load partners', error);
            setError('Failed to load partners: ' + getApiErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const loadActivity = async () => {
        try {
            const response = await api.getPluginActivity({ limit: 10 });
            setActivity(response.data.data?.activities || []);
        } catch (error) {
            logError('PluginPage - Failed to load activity', error);
        }
    };

    const loadFlowConfigs = async () => {
        try {
            const response = await api.getPluginFlowConfigs();
            setFlowConfigs(response.data.data?.flowConfigs || []);
            
            // Also load flow stats
            const statsResponse = await api.getPluginFlowStats();
            setFlowStats(statsResponse.data.data || statsResponse.data);
        } catch (error) {
            logError('PluginPage - Failed to load flow configs', error);
        }
    };

    const loadPluginSettings = async (partnerId) => {
        try {
            setLoading(true);
            const response = await api.getPluginPartner(partnerId);
            const partner = response.data.data?.partner || response.data;
            
            // Load existing plugin settings or use defaults
            if (partner.pluginSettings) {
                setPluginSettings({
                    colorScheme: partner.pluginSettings.colorScheme || 'blue-purple',
                    flowAlias: partner.pluginSettings.flowAlias || 'Guides',
                    position: partner.pluginSettings.position || { right: '20px', bottom: '20px' },
                    enableFlows: partner.pluginSettings.enableFlows !== false,
                    enableReferrals: partner.pluginSettings.enableReferrals !== false,
                    enableRewards: partner.pluginSettings.enableRewards !== false,
                    enableSupport: partner.pluginSettings.enableSupport !== false,
                    overlay: {
                        enabled: partner.pluginSettings.overlay?.enabled || false,
                        requirePassword: partner.pluginSettings.overlay?.requirePassword || false,
                        password: partner.pluginSettings.overlay?.password || '',
                        showTeaser: partner.pluginSettings.overlay?.showTeaser !== false,
                        teaserMessage: partner.pluginSettings.overlay?.teaserMessage || 'Welcome! Enter your access code to unlock exclusive features.'
                    }
                });
            } else {
                // Reset to defaults if no settings exist
                setPluginSettings({
                    colorScheme: 'blue-purple',
                    flowAlias: 'Guides',
                    position: { right: '20px', bottom: '20px' },
                    enableFlows: true,
                    enableReferrals: true,
                    enableRewards: true,
                    enableSupport: true,
                    overlay: {
                        enabled: false,
                        requirePassword: false,
                        password: '',
                        showTeaser: true,
                        teaserMessage: 'Welcome! Enter your access code to unlock exclusive features.'
                    }
                });
            }
        } catch (error) {
            logError('PluginPage - Failed to load plugin settings', error);
            setError('Failed to load plugin settings: ' + getApiErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const handlePartnerChange = (e) => {
        const partnerId = e.target.value;
        setSelectedPartnerId(partnerId);
        setError(null);
        setSuccessMessage('');
        
        if (partnerId) {
            loadPluginSettings(partnerId);
        }
    };

    const handleSettingChange = (field, value) => {
        setPluginSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const savePluginSettings = async (e) => {
        e.preventDefault();
        
        if (!selectedPartnerId) {
            setError('Please select a partner');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            await api.updatePartnerPluginSettings(selectedPartnerId, pluginSettings);
            
            setSuccessMessage('Plugin settings saved successfully!');
        } catch (error) {
            logError('PluginPage - Failed to save plugin settings', error);
            setError('Failed to save plugin settings: ' + getApiErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const updateFlowConfig = async (flowId, updates) => {
        try {
            await api.updatePluginFlowConfig(flowId, updates);
            setSuccessMessage('Flow configuration updated successfully!');
            loadFlowConfigs(); // Reload flow configs
        } catch (error) {
            logError('PluginPage - Failed to update flow config', error);
            setError('Failed to update flow configuration: ' + getApiErrorMessage(error));
        }
    };

    if (!adminUser) return <div>Loading...</div>;

    // Sidebar menu items
    const sidebarItems = [
        { id: 'dashboard', icon: '📊', label: 'Dashboard' },
        { id: 'partners', icon: '🏢', label: 'Partners' },
        { id: 'flows', icon: '🔄', label: 'Flows' },
        { id: 'analytics', icon: '📈', label: 'Analytics' }
    ];

    const renderDashboard = () => (
        <div>
            <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '600', color: '#111827' }}>
                Plugin Dashboard
            </h2>
            
            {/* Stats Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
                <div style={{
                    backgroundColor: '#fff',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <p style={{ margin: '0', fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Total Partners</p>
                            <p style={{ margin: '8px 0 0 0', fontSize: '32px', fontWeight: '700', color: '#111827' }}>
                                {dashboardData?.totalPartners || 0}
                            </p>
                        </div>
                        <div style={{ fontSize: '32px' }}>🏢</div>
                    </div>
                </div>
                
                <div style={{
                    backgroundColor: '#fff',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <p style={{ margin: '0', fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Active Plugins</p>
                            <p style={{ margin: '8px 0 0 0', fontSize: '32px', fontWeight: '700', color: '#111827' }}>
                                {dashboardData?.activePlugins || 0}
                            </p>
                        </div>
                        <div style={{ fontSize: '32px' }}>🔌</div>
                    </div>
                </div>
                
                <div style={{
                    backgroundColor: '#fff',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <p style={{ margin: '0', fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Monthly Sessions</p>
                            <p style={{ margin: '8px 0 0 0', fontSize: '32px', fontWeight: '700', color: '#111827' }}>
                                {dashboardData?.monthlySessions || 0}
                            </p>
                        </div>
                        <div style={{ fontSize: '32px' }}>📊</div>
                    </div>
                </div>
                
                <div style={{
                    backgroundColor: '#fff',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <p style={{ margin: '0', fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Flow Completions</p>
                            <p style={{ margin: '8px 0 0 0', fontSize: '32px', fontWeight: '700', color: '#111827' }}>
                                {dashboardData?.flowCompletions || 0}
                            </p>
                        </div>
                        <div style={{ fontSize: '32px' }}>✅</div>
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
                <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                        Recent Activity
                    </h3>
                </div>
                <div style={{ padding: '0' }}>
                    {activity.length > 0 ? (
                        activity.map((item, index) => (
                            <div key={index} style={{
                                padding: '16px 24px',
                                borderBottom: index < activity.length - 1 ? '1px solid #f3f4f6' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <div>
                                    <p style={{ margin: '0', fontSize: '14px', color: '#111827', fontWeight: '500' }}>
                                        {item.description}
                                    </p>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                                        {item.partner} • {new Date(item.timestamp).toLocaleString()}
                                    </p>
                                </div>
                                <div style={{
                                    padding: '4px 8px',
                                    backgroundColor: item.type === 'success' ? '#dcfce7' : '#fef3c7',
                                    color: item.type === 'success' ? '#166534' : '#92400e',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500'
                                }}>
                                    {item.type}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: '40px 24px', textAlign: 'center', color: '#6b7280' }}>
                            No recent activity
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderPartners = () => (
        <div>
            <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '600', color: '#111827' }}>
                Partner Management
            </h2>
            
            {/* Partner Selection */}
            <div style={{
                backgroundColor: '#fff',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                marginBottom: '24px'
            }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Select Partner Organization
                </label>
                <select
                    value={selectedPartnerId}
                    onChange={handlePartnerChange}
                    style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: '#fff'
                    }}
                >
                    <option value="">Select a partner...</option>
                    {partners.map(partner => (
                        <option key={partner._id} value={partner._id}>
                            {partner.displayName || partner.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Plugin Settings Form */}
            {selectedPartnerId && (
                <form onSubmit={savePluginSettings}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '24px',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                            Plugin Configuration
                        </h3>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                            {/* Color Scheme */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                    Color Scheme
                                </label>
                                <select
                                    value={pluginSettings.colorScheme}
                                    onChange={(e) => handleSettingChange('colorScheme', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        backgroundColor: '#fff'
                                    }}
                                >
                                    <option value="orange-red">Orange Red</option>
                                    <option value="blue-purple">Blue Purple</option>
                                    <option value="green-teal">Green Teal</option>
                                </select>
                            </div>
                            
                            {/* Flow Alias */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                    Flow Menu Label
                                </label>
                                <input
                                    type="text"
                                    value={pluginSettings.flowAlias}
                                    onChange={(e) => handleSettingChange('flowAlias', e.target.value)}
                                    placeholder="e.g., Guides, Tutorials, Help"
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}
                                />
                                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                                    Custom label for the flows menu item (defaults to "Guides")
                                </p>
                            </div>
                        </div>

                        {/* Position Settings */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                Assistant Position
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                                <div>
                                    <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>Right</label>
                                    <input
                                        type="text"
                                        value={pluginSettings.position?.right || ''}
                                        onChange={(e) => handleSettingChange('position', { ...pluginSettings.position, right: e.target.value })}
                                        placeholder="20px"
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>Bottom</label>
                                    <input
                                        type="text"
                                        value={pluginSettings.position?.bottom || ''}
                                        onChange={(e) => handleSettingChange('position', { ...pluginSettings.position, bottom: e.target.value })}
                                        placeholder="20px"
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>Left</label>
                                    <input
                                        type="text"
                                        value={pluginSettings.position?.left || ''}
                                        onChange={(e) => handleSettingChange('position', { ...pluginSettings.position, left: e.target.value })}
                                        placeholder="auto"
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>Top</label>
                                    <input
                                        type="text"
                                        value={pluginSettings.position?.top || ''}
                                        onChange={(e) => handleSettingChange('position', { ...pluginSettings.position, top: e.target.value })}
                                        placeholder="auto"
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Feature Toggles */}
                        <div style={{ marginBottom: '32px' }}>
                            <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                Feature Settings
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                                {[
                                    { key: 'enableFlows', label: 'Flows/Guides', icon: '🔄' },
                                    { key: 'enableReferrals', label: 'Referrals', icon: '👥' },
                                    { key: 'enableRewards', label: 'Rewards', icon: '🎁' },
                                    { key: 'enableSupport', label: 'Support', icon: '💬' }
                                ].map(feature => (
                                    <label key={feature.key} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '12px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        backgroundColor: pluginSettings[feature.key] ? '#f0f9ff' : '#f9fafb'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={pluginSettings[feature.key]}
                                            onChange={(e) => handleSettingChange(feature.key, e.target.checked)}
                                            style={{ width: '16px', height: '16px' }}
                                        />
                                        <span style={{ fontSize: '20px' }}>{feature.icon}</span>
                                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                                            {feature.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Overlay/Welcome Screen Configuration */}
                        <div style={{ 
                            marginBottom: '32px',
                            padding: '20px',
                            backgroundColor: '#fef3c7',
                            border: '1px solid #fbbf24',
                            borderRadius: '8px'
                        }}>
                            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
                                🔒 Overlay / Welcome Screen Configuration
                            </h4>
                            
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '12px',
                                    border: '1px solid #fbbf24',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    backgroundColor: pluginSettings.overlay?.enabled ? '#fef3c7' : '#fff'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={pluginSettings.overlay?.enabled || false}
                                        onChange={(e) => handleSettingChange('overlay', { ...pluginSettings.overlay, enabled: e.target.checked })}
                                        style={{ width: '16px', height: '16px' }}
                                    />
                                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#92400e' }}>
                                        Enable CNXN Overlay/Blocker
                                    </span>
                                </label>
                                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#92400e' }}>
                                    When enabled, shows an initial overlay before the partner's welcome screen
                                </p>
                            </div>

                            {pluginSettings.overlay?.enabled && (
                                <>
                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '12px',
                                            border: '1px solid #fbbf24',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            backgroundColor: pluginSettings.overlay?.requirePassword ? '#fef3c7' : '#fff'
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={pluginSettings.overlay?.requirePassword || false}
                                                onChange={(e) => handleSettingChange('overlay', { ...pluginSettings.overlay, requirePassword: e.target.checked })}
                                                style={{ width: '16px', height: '16px' }}
                                            />
                                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#92400e' }}>
                                                Require Access Code
                                            </span>
                                        </label>
                                        <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#92400e' }}>
                                            Users must enter a code to access the site
                                        </p>
                                    </div>

                                    {pluginSettings.overlay?.requirePassword && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                                                Access Code
                                            </label>
                                            <input
                                                type="text"
                                                value={pluginSettings.overlay?.password || ''}
                                                onChange={(e) => handleSettingChange('overlay', { ...pluginSettings.overlay, password: e.target.value })}
                                                placeholder="e.g., rsvp4x, welcome2024"
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '300px',
                                                    padding: '12px',
                                                    border: '1px solid #fbbf24',
                                                    borderRadius: '8px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#fff'
                                                }}
                                            />
                                        </div>
                                    )}

                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '12px',
                                            border: '1px solid #fbbf24',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            backgroundColor: pluginSettings.overlay?.showTeaser ? '#fef3c7' : '#fff'
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={pluginSettings.overlay?.showTeaser !== false}
                                                onChange={(e) => handleSettingChange('overlay', { ...pluginSettings.overlay, showTeaser: e.target.checked })}
                                                style={{ width: '16px', height: '16px' }}
                                            />
                                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#92400e' }}>
                                                Show Teaser Message
                                            </span>
                                        </label>
                                        <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#92400e' }}>
                                            Display a brief teaser before showing the full welcome screen
                                        </p>
                                    </div>

                                    {pluginSettings.overlay?.showTeaser && (
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                                                Teaser Message
                                            </label>
                                            <textarea
                                                value={pluginSettings.overlay?.teaserMessage || ''}
                                                onChange={(e) => handleSettingChange('overlay', { ...pluginSettings.overlay, teaserMessage: e.target.value })}
                                                placeholder="Enter a brief welcome message..."
                                                rows="3"
                                                style={{
                                                    width: '100%',
                                                    padding: '12px',
                                                    border: '1px solid #fbbf24',
                                                    borderRadius: '8px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#fff',
                                                    fontFamily: 'inherit',
                                                    resize: 'vertical'
                                                }}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        
                        {/* Save Button */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: '#4f46e5',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    opacity: loading ? 0.6 : 1,
                                    transition: 'all 0.2s'
                                }}
                            >
                                {loading ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </div>
    );

    const renderFlows = () => (
        <div>
            <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '600', color: '#111827' }}>
                Flow Configuration
            </h2>
            
            {/* Flow Stats */}
            {flowStats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                        <p style={{ margin: '0', fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>
                            Total Flows
                        </p>
                        <p style={{ margin: '8px 0 0 0', fontSize: '24px', fontWeight: '700', color: '#111827' }}>
                            {flowStats.totalFlows}
                        </p>
                    </div>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                        <p style={{ margin: '0', fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>
                            Completion Rate
                        </p>
                        <p style={{ margin: '8px 0 0 0', fontSize: '24px', fontWeight: '700', color: '#111827' }}>
                            {flowStats.completionRate}%
                        </p>
                    </div>
                </div>
            )}
            
            {/* Flow List */}
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
                <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                        Available Flows
                    </h3>
                </div>
                <div style={{ padding: '0' }}>
                    {flowConfigs.length > 0 ? (
                        flowConfigs.map((flow, index) => (
                            <div key={flow._id} style={{
                                padding: '20px 24px',
                                borderBottom: index < flowConfigs.length - 1 ? '1px solid #f3f4f6' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                                        {flow.name}
                                    </h4>
                                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                                        {flow.description}
                                    </p>
                                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280' }}>
                                        <span>Steps: {flow.steps?.length || 0}</span>
                                        <span>Points: {flow.rewardPoints || 0}</span>
                                        <span>Category: {flow.category || 'General'}</span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                        <input
                                            type="checkbox"
                                            checked={flow.canRerun !== false}
                                            onChange={(e) => updateFlowConfig(flow._id, { canRerun: e.target.checked })}
                                        />
                                        <span>Can Rerun</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                        <input
                                            type="checkbox"
                                            checked={flow.isActive !== false}
                                            onChange={(e) => updateFlowConfig(flow._id, { isActive: e.target.checked })}
                                        />
                                        <span>Active</span>
                                    </label>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: '40px 24px', textAlign: 'center', color: '#6b7280' }}>
                            No flows configured
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderAnalytics = () => (
        <div>
            <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '600', color: '#111827' }}>
                Analytics & Reports
            </h2>
            
            <div style={{
                backgroundColor: '#fff',
                padding: '40px 24px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                textAlign: 'center'
            }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>📊</div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#111827' }}>
                    Analytics Coming Soon
                </h3>
                <p style={{ margin: '0', color: '#6b7280' }}>
                    Detailed analytics and reporting features will be available in the next update.
                </p>
            </div>
        </div>
    );

    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard': return renderDashboard();
            case 'partners': return renderPartners();
            case 'flows': return renderFlows();
            case 'analytics': return renderAnalytics();
            default: return renderDashboard();
        }
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
            <AdminHeader adminUser={adminUser} />
            
            <div style={{ display: 'flex', maxWidth: '1200px', margin: '0 auto' }}>
                {/* Sidebar */}
                <div style={{
                    width: '260px',
                    backgroundColor: '#fff',
                    borderRight: '1px solid #e5e7eb',
                    minHeight: 'calc(100vh - 140px)',
                    padding: '24px 0'
                }}>
                    <div style={{ padding: '0 24px', marginBottom: '24px' }}>
                        <h2 style={{ margin: '0', fontSize: '18px', fontWeight: '700', color: '#111827' }}>
                            Plugin Management
                        </h2>
                        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                            Configure and monitor plugin settings
                        </p>
                    </div>
                    
                    <nav>
                        {sidebarItems.map(item => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    setActiveTab(item.id);
                                    setError(null);
                                    setSuccessMessage('');
                                }}
                                style={{
                                    width: '100%',
                                    padding: '12px 24px',
                                    border: 'none',
                                    backgroundColor: activeTab === item.id ? '#f0f9ff' : 'transparent',
                                    color: activeTab === item.id ? '#0369a1' : '#6b7280',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    fontSize: '14px',
                                    fontWeight: activeTab === item.id ? '600' : '500',
                                    borderRight: activeTab === item.id ? '3px solid #0369a1' : '3px solid transparent',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <span style={{ fontSize: '16px' }}>{item.icon}</span>
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>
                
                {/* Main Content */}
                <div style={{ flex: 1, padding: '24px' }}>
                    {/* Status Messages */}
                    {error && (
                        <div style={{
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fca5a5',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            marginBottom: '24px',
                            color: '#991b1b',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}
                    
                    {successMessage && (
                        <div style={{
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #86efac',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            marginBottom: '24px',
                            color: '#166534',
                            fontSize: '14px'
                        }}>
                            {successMessage}
                        </div>
                    )}
                    
                    {renderContent()}
                </div>
            </div>
        </div>
    );
}

export default PluginPage;